import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import type { Dispatch, SetStateAction } from 'react';
import type { SnackbarAction } from 'notistack';

import type { Id } from '@e-socna/data-layer';

import { NotiStackButton } from '../../../components/NotiStackButton';
import { PATH_NOT_FOUND } from '../../../app/constants/paths.constant';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { memoForm2ApiMapper } from '../../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import type { EditorModePaths } from '../../../components/DocumentEditor/types/props.type';
import type { MemosState } from '../../../forms/MemoBeforeSubmit';
import type { BackError } from '../../../utils/back-errors.util';

import { documentTemplateForm2ApiMapper } from '../../NewDocumentTemplate/mappers/document-template.form2api.mapper';
import { MessageList } from '../../../components/MessageList';

import { useFoapuDocumentTemplateMutation } from './useUpdateMutation.hook';
import { useQueryInvalidation } from './useQueryInvalidation.hook';
import type { DocumentTemplatePage_OneDocumentTemplate } from './useFindOneDocumentTemplateQuery.hook';
import type { DocumentTemplate_Foapu_Result } from './useUpdateMutation.hook';

interface Params {
  readonly documentTemplateId: Id;
  readonly editorModePaths: EditorModePaths;
  readonly setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const useSaveHandler = ({
  documentTemplateId,
  editorModePaths,
  setIsLoading,
}: Readonly<Params>) => {
  const { invalidateQueries } = useQueryInvalidation(documentTemplateId);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const { mutate: updateDocumentTemplateMutation } =
    useFoapuDocumentTemplateMutation();

  const onSave = useCallback(
    (
      values: DocumentTemplatePage_OneDocumentTemplate,
      memos: MemosState,
      handleSuccess?: (data: DocumentTemplate_Foapu_Result) => void,
      handleError?: () => void,
      // eslint-disable-next-line max-params
    ) => {
      setIsLoading(true);

      const onSuccess = (data: DocumentTemplate_Foapu_Result) => {
        setIsLoading(false);

        enqueueSnackbar('Votre modèle de document a bien été mis à jour.');

        invalidateQueries();

        handleSuccess?.(data);
      };

      const onError = (error: unknown) => {
        setIsLoading(false);
        const backError = error as BackError;
        let action: SnackbarAction | null = null;

        const supportedErrors = [
          'NO_CODE_OR_LABEL',
          'CODE_ALREADY_USED',
          'EMPTY_SECTION',
        ];

        const supportedErrorCode = backError.response.errors.find(
          (error) =>
            error.extensions.errCode &&
            supportedErrors.includes(error.extensions.errCode),
        )?.extensions.errCode;

        if (supportedErrorCode) {
          let to = editorModePaths.Editor;

          switch (supportedErrorCode) {
            case 'NO_CODE_OR_LABEL':
              to = editorModePaths.InfoEdit;
              break;
            case 'CODE_ALREADY_USED':
              to = editorModePaths.InfoEdit;
              break;
            case 'EMPTY_SECTION':
              to = editorModePaths.Editor;
              break;
            default:
              console.warn(
                `Unknown error code in DocumentTemplate useSaveHandler: ${supportedErrorCode}`,
              );
          }

          action = (snackbarId) => (
            <NotiStackButton
              // eslint-disable-next-line react/jsx-no-bind
              onClick={() => {
                void navigate(to ?? PATH_NOT_FOUND);
                closeSnackbar(snackbarId);
              }}
              variant="text"
            >
              Modifier
            </NotiStackButton>
          );
        }

        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
            action,
          },
        );

        handleError?.();
      };

      updateDocumentTemplateMutation(
        {
          filter: { _id: documentTemplateId },
          update: documentTemplateForm2ApiMapper(values),
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError,
          onSuccess,
        },
      );
    },
    [
      closeSnackbar,
      documentTemplateId,
      editorModePaths.Editor,
      editorModePaths.InfoEdit,
      enqueueSnackbar,
      invalidateQueries,
      navigate,
      setIsLoading,
      updateDocumentTemplateMutation,
    ],
  );

  return { onSave };
};
